import React, { ReactNode } from 'react';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Paper, SxProps, Theme, Typography } from '@mui/material';

import { assertExhaustiveSwitch } from '@paragon/utilities';

export type InlineNotificationType = 'error' | 'info' | 'warning';

interface IconProps {
  readonly type: InlineNotificationType;
}

function Icon({ type }: IconProps) {
  switch (type) {
    case 'error':
      return <ErrorIcon sx={{ mr: 1 }} />;
    case 'info':
      return <InfoIcon sx={{ mr: 1 }} />;
    case 'warning':
      return <WarningIcon sx={{ mr: 1 }} />;
    default:
      assertExhaustiveSwitch(type, 'Unexpected notification type.');
  }
}

interface Props {
  readonly children?: ReactNode;
  readonly sx?: SxProps<Theme>;
  readonly noIcon?: boolean;
  readonly type: InlineNotificationType;

  readonly fullWidth?: boolean;
  readonly fullWidthWrap?: boolean;
}

export function InlineNotification({ children, fullWidth, fullWidthWrap, noIcon, sx = [], type }: Props) {
  const result = (
    <Paper
      sx={[
        { mt: 2, p: 1 },
        type === 'error' ? { bgcolor: 'error.light' } : null,
        type === 'info' ? { bgcolor: '#657FF4' } : null,
        type === 'warning' ? { bgcolor: '#D5922D', color: '#000' } : null,
        fullWidth ? { width: '100%' } : null,
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Typography sx={{ display: 'flex', alignItems: 'center' }}>
        {noIcon !== true && <Icon type={type} />}
        {children}
      </Typography>
    </Paper>
  );

  if (fullWidthWrap) {
    return <Box sx={{ width: 0, minWidth: '100%' }}>{result}</Box>;
  }

  return result;
}
